import { Component, OnInit, OnDestroy, inject, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatDialogModule } from "@angular/material/dialog";
import { HttpErrorResponse } from "@angular/common/http";
import {
  catchError,
  of,
  take,
  forkJoin,
  map,
  switchMap,
  throwError,
  Observable,
} from "rxjs";
import {
  HardBtnComponent,
  HardFormModule,
  HardFormService,
  HardLoadingComponent,
  HardModalComponent,
  HardNoDataComponent,
  HardTranslatePipe,
  IHardFormField,
} from "@hardcode-kz/ui";
import { DictionaryService } from "@dmms/core/http";
import { NotificationService } from "@dmms/core/notification";
import { FileStorageApiService } from "@dmms/shared/data-access/file-storage/services/file-storage-api.service";
import { BaseModalComponent } from "@modals/base-modal/base-modal.component";
import { IBaseConfig } from "@models/form/base-config";
import {
  ApiErrorFieldEnum,
  IApiResponse,
  IBaseHttpResponse,
} from "@models/http/http-options";
import {
  IGuidePayload,
  IGuideResponse,
} from "@models/interface-management/guide";
import { IDicBase } from "@models/dictionary/dic-base";
import { LangEnum } from "@models/interface-management/localization";

export interface IGuideModalData {
  config: IBaseConfig;
  apiUrl: string;
  item?: IGuideResponse;
}

@Component({
  selector: "app-guide-modal",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    HardModalComponent,
    HardBtnComponent,
    HardFormModule,
    HardLoadingComponent,
    HardNoDataComponent,
    HardTranslatePipe,
  ],
  templateUrl: "./guide-modal.component.html",
  styleUrl: "./guide-modal.component.scss",
})
export class GuideModalComponent
  extends BaseModalComponent<GuideModalComponent, IGuideModalData>
  implements OnInit, OnDestroy
{
  form!: FormGroup;
  loading = true;
  saving = false;
  item: IGuideResponse | null = null;

  gridFields: IHardFormField[] = [];

  // Files state
  videoFile: File | null = null;
  ruPdfFile: File | null = null;
  kzPdfFile: File | null = null;
  enPdfFile: File | null = null;

  filesToDelete: string[] = [];

  videoSrc = signal<string | null>(null);

  // Loading flags for templates
  ruUploading = signal(false);
  kzUploading = signal(false);
  enUploading = signal(false);
  videoUploading = signal(false);

  private readonly fb = inject(FormBuilder);
  private readonly formService = inject(HardFormService);
  private readonly dicService = inject(DictionaryService);
  private readonly fileStorageApiService = inject(FileStorageApiService);
  private readonly notificationService = inject(NotificationService);

  ngOnInit(): void {
    this.gridFields = this.data.config.fields || [];
    this.form = this.formService.buildReactiveForm(this.gridFields);

    this.form.addControl("videoUrl", this.fb.control(null));
    this.form.addControl("ruDocumentUrl", this.fb.control(null));
    this.form.addControl("kzDocumentUrl", this.fb.control(null));
    this.form.addControl("enDocumentUrl", this.fb.control(null));

    if (this.data.item) {
      this.loading = true;
      this.dicService
        .getItem(this.data.apiUrl, this.data.item.id)
        .pipe(
          take(1),
          catchError((err: HttpErrorResponse) => {
            this.notificationService.findAndShowErrors(
              err,
              ApiErrorFieldEnum.Code,
            );
            this.loading = false;
            return of(null);
          }),
        )
        .subscribe((res: IApiResponse<IGuideResponse>) => {
          if (res?.isSuccess && res.value) {
            const item = res.value;
            this.item = item;

            this.form.patchValue({
              name: item.name,
              videoUrl: item.videoUrl || null,
              ruDocumentUrl: item.documentUrl?.ru || null,
              kzDocumentUrl: item.documentUrl?.kz || null,
              enDocumentUrl: item.documentUrl?.en || null,
              roles: item?.roles,
            });

            if (item.videoUrl) {
              this.loadVideo(item.videoUrl);
            }
          }
          this.loading = false;
        });
    } else {
      this.loading = false;
    }
  }

  private revokeVideoSrc(): void {
    const src = this.videoSrc();
    if (src) {
      window.URL.revokeObjectURL(src);
      this.videoSrc.set(null);
    }
  }

  ngOnDestroy(): void {
    this.revokeVideoSrc();
  }

  loadVideo(path: string): void {
    this.revokeVideoSrc();
    if (!path) {
      return;
    }
    this.fileStorageApiService
      .getFile(path)
      .pipe(take(1))
      .subscribe({
        next: (blob) => {
          if (blob) {
            this.revokeVideoSrc();
            this.videoSrc.set(window.URL.createObjectURL(blob));
          }
        },
        error: () => {
          this.revokeVideoSrc();
        },
      });
  }

  onVideoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.videoFile = file;
    this.revokeVideoSrc();
    this.videoSrc.set(window.URL.createObjectURL(file));

    const currentVideo = this.item?.videoUrl;
    if (currentVideo && !this.filesToDelete.includes(currentVideo)) {
      this.filesToDelete.push(currentVideo);
    }

    this.form.patchValue({ videoUrl: file.name });
  }

  removeVideo(): void {
    const currentVideo = this.item?.videoUrl;
    if (currentVideo && !this.filesToDelete.includes(currentVideo)) {
      this.filesToDelete.push(currentVideo);
    }
    this.videoFile = null;
    this.revokeVideoSrc();
    this.form.patchValue({ videoUrl: null });
  }

  uploadPdf(event: Event, lang: LangEnum): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const controlName = `${lang}DocumentUrl`;
    const currentPdf = this.item?.documentUrl?.[lang];
    if (currentPdf && !this.filesToDelete.includes(currentPdf)) {
      this.filesToDelete.push(currentPdf);
    }

    if (lang === LangEnum.RU) {
      this.ruPdfFile = file;
    } else if (lang === LangEnum.KZ) {
      this.kzPdfFile = file;
    } else {
      this.enPdfFile = file;
    }

    this.form.patchValue({ [controlName]: file.name });
  }

  removePdf(lang: LangEnum): void {
    const controlName = `${lang}DocumentUrl`;
    const currentPdf = this.item?.documentUrl?.[lang];
    if (currentPdf && !this.filesToDelete.includes(currentPdf)) {
      this.filesToDelete.push(currentPdf);
    }

    if (lang === LangEnum.RU) {
      this.ruPdfFile = null;
    } else if (lang === LangEnum.KZ) {
      this.kzPdfFile = null;
    } else {
      this.enPdfFile = null;
    }

    this.form.patchValue({ [controlName]: null });
  }

  save(): void {
    if (this.saving || this.loading || this.form?.invalid) return;

    this.saving = true;

    // 1. Delete removed files first
    const deleteOps = this.filesToDelete.map((path) =>
      this.fileStorageApiService.deletePhoto(path).pipe(
        catchError(() => of(null)), // Ignore delete errors to keep transaction running
      ),
    );

    // 2. Upload new files
    const uploadOps: Observable<{ key: string; path: string }>[] = [];

    if (this.videoFile) {
      uploadOps.push(
        this.fileStorageApiService
          .uploadVideo(this.videoFile)
          .pipe(map((path) => ({ key: "videoUrl", path }))),
      );
    }
    if (this.ruPdfFile) {
      uploadOps.push(
        this.fileStorageApiService
          .uploadPhoto(this.ruPdfFile)
          .pipe(map((path) => ({ key: "ruDocumentUrl", path }))),
      );
    }
    if (this.kzPdfFile) {
      uploadOps.push(
        this.fileStorageApiService
          .uploadPhoto(this.kzPdfFile)
          .pipe(map((path) => ({ key: "kzDocumentUrl", path }))),
      );
    }
    if (this.enPdfFile) {
      uploadOps.push(
        this.fileStorageApiService
          .uploadPhoto(this.enPdfFile)
          .pipe(map((path) => ({ key: "enDocumentUrl", path }))),
      );
    }

    const deletes$ = deleteOps.length > 0 ? forkJoin(deleteOps) : of([]);

    deletes$
      .pipe(
        switchMap(() => (uploadOps.length > 0 ? forkJoin(uploadOps) : of([]))),
        take(1),
        catchError((err) => {
          this.notificationService.error("Ошибка при загрузке файлов");
          this.saving = false;
          return throwError(() => err);
        }),
      )
      .subscribe((uploadedFiles) => {
        const formValue = this.form.getRawValue();

        uploadedFiles.forEach((file) => {
          formValue[file.key] = file.path;
        });

        // Construct backend payload
        const payload: IGuidePayload & { id?: string } = {
          name: formValue.name,
          roles: formValue.roles?.map((item: IDicBase) => item?.id) || [],
          videoUrl: formValue.videoUrl || "",
          documentUrl: {
            ru: formValue.ruDocumentUrl || "",
            kz: formValue.kzDocumentUrl || "",
            en: formValue.enDocumentUrl || "",
          },
        };

        if (this.item) {
          payload.id = this.item.id;
        }

        const api$ = this.item
          ? this.dicService.updateItem(
              this.data.apiUrl,
              payload,
              this.data.config?.version || 1,
              this.data.config?.updateMethod,
            )
          : this.dicService.saveItem(this.data.apiUrl, payload);

        api$
          .pipe(
            take(1),
            catchError((errorRes: HttpErrorResponse) => {
              this.notificationService.findAndShowErrors(
                errorRes,
                ApiErrorFieldEnum.Code,
              );
              this.saving = false;
              return of(null);
            }),
          )
          .subscribe((res: IBaseHttpResponse) => {
            if (res?.isSuccess) {
              this.notificationService.success(
                this.item ? "ui.form.editSuccess" : "ui.form.saveSuccess",
              );
              this.close(true);
            }
            this.saving = false;
          });
      });
  }

  protected readonly LangEnum = LangEnum;
}
